import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ShoppingCart } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { toast } from '../../utils/toast'

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const navigate = useNavigate()
  const login = useAuthStore((state) => state.login)
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    // Previne múltiplas submissões
    if (isLoading) {
      console.log('⚠️ Login já em andamento, ignorando nova tentativa')
      return
    }

    console.log('🔐 Iniciando login...')
    setIsLoading(true)
    
    try {
      await login(data.email, data.password)
      console.log('✅ Login bem-sucedido')
      toast.success('Login realizado com sucesso!')
      navigate('/dashboard')
    } catch (error) {
      console.error('❌ Erro no login:', error)
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer login')
      setIsLoading(false) // Libera para nova tentativa apenas em caso de erro
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="w-full max-w-md">
        <div className="rounded-lg bg-white p-8 shadow-xl dark:bg-gray-800">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center">
            <div className="flex items-center justify-center rounded-full bg-primary-100 p-3 dark:bg-primary-900/20">
              <ShoppingCart className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
              OrderSystem
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Sistema de Centralização de Pedidos
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="E-mail"
              type="email"
              placeholder="seu@email.com"
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label="Senha"
              type="password"
              placeholder="••••••••"
              error={errors.password?.message}
              {...register('password')}
            />

            <Button
              type="submit"
              className="w-full"
              isLoading={isLoading}
              disabled={isLoading}
            >
              Entrar
            </Button>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-gray-600 dark:text-gray-400">
          © {new Date().getFullYear()} SoftTech Cloud. Todos os direitos reservados.
        </p>
      </div>
    </div>
  )
}
